// PWA install support. Two concerns:
//   1) PURE: detect the platform + resolve what install affordance to show
//      (a real native prompt, a manual "Add to Home Screen" guide, or nothing
//      when already installed). All deterministic + injectable, so it is unit
//      tested without a browser.
//   2) CAPTURE: a tiny singleton that intercepts the one-shot
//      `beforeinstallprompt` event (Android / desktop Chromium) so we can fire
//      it from our own button in the Tools menu instead of the browser's
//      mini-infobar. iOS never fires this event - there the guide is the only
//      path (Safari Share -> Add to Home Screen).
//
// ASCII-only copy (project rule): no Unicode arrows / ellipsis.

export type InstallPlatform = "ios" | "android" | "desktop";

export interface PlatformInput {
  ua: string; // navigator.userAgent
  maxTouchPoints?: number; // navigator.maxTouchPoints - distinguishes iPadOS-as-Mac
}

// iPadOS 13+ reports a desktop "Macintosh" UA; the touch-point count is the
// standard tell that it is really a touch iPad (real Macs report 0).
export function detectPlatform(input: PlatformInput): InstallPlatform {
  const ua = input.ua;
  if (/iphone|ipod|ipad/i.test(ua)) return "ios";
  if (/macintosh/i.test(ua) && (input.maxTouchPoints ?? 0) > 1) return "ios";
  if (/android/i.test(ua)) return "android";
  return "desktop";
}

export interface InstallGuide {
  platform: InstallPlatform;
  title: string;
  steps: string[];
}

// Per-platform manual install instructions. Plain, device-accurate language;
// the steps name the exact buttons the user is looking for.
export function installGuide(platform: InstallPlatform): InstallGuide {
  switch (platform) {
    case "ios":
      return {
        platform,
        title: "Add to your iPhone",
        steps: [
          "In Safari, tap the Share button (the square with an up arrow).",
          'Scroll down and choose "Add to Home Screen".',
          'Tap "Add" - TorahTrail opens like an app, full screen.',
        ],
      };
    case "android":
      return {
        platform,
        title: "Add to your Android phone",
        steps: [
          "In Chrome, tap the menu (three dots, top right).",
          'Choose "Install app" or "Add to Home screen".',
          "Confirm - TorahTrail opens like an app.",
        ],
      };
    case "desktop":
      return {
        platform,
        title: "Install on your computer",
        steps: [
          "In Chrome or Edge, click the install icon at the right of the address bar.",
          'Choose "Install".',
          "TorahTrail opens in its own window.",
        ],
      };
  }
}

export type InstallMode =
  | { kind: "installed" }
  | { kind: "prompt" }
  | { kind: "guide"; guide: InstallGuide };

export interface InstallModeInput {
  standalone: boolean;
  hasPrompt: boolean;
  platform: InstallPlatform;
}

// Resolve which affordance to show. Installed wins (nothing to offer); else a
// captured native prompt; else the manual guide for the platform.
export function installMode(input: InstallModeInput): InstallMode {
  if (input.standalone) return { kind: "installed" };
  if (input.hasPrompt) return { kind: "prompt" };
  return { kind: "guide", guide: installGuide(input.platform) };
}

// Already running as an installed app? display-mode:standalone covers Android /
// desktop; navigator.standalone is iOS Safari's own flag.
export function isStandalone(win: Window = window, nav: Navigator = navigator): boolean {
  const mm = win.matchMedia?.("(display-mode: standalone)").matches ?? false;
  const iosStandalone = (nav as Navigator & { standalone?: boolean }).standalone === true;
  return mm || iosStandalone;
}

// The non-standard event Chromium fires when the app is installable.
export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

// Capture singleton. The event fires once, globally, often before any view has
// mounted - so we stash it and let the Tools menu read it on each (re)build.
let deferredPrompt: BeforeInstallPromptEvent | null = null;

export function initInstallCapture(target: EventTarget = window): void {
  target.addEventListener("beforeinstallprompt", (e) => {
    // Suppress the browser mini-infobar; we surface our own button instead.
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
  });
  target.addEventListener("appinstalled", () => {
    deferredPrompt = null; // installed - drop the stale prompt (isStandalone takes over)
  });
}

export function hasDeferredPrompt(): boolean {
  return deferredPrompt !== null;
}

// Fire the captured native prompt and report the outcome. The captured event is
// single-use per spec, so it is cleared whether the user accepts or dismisses.
export async function runInstallPrompt(): Promise<"accepted" | "dismissed" | "unavailable"> {
  const e = deferredPrompt;
  if (!e) return "unavailable";
  deferredPrompt = null;
  await e.prompt();
  const { outcome } = await e.userChoice;
  return outcome;
}

// Convenience for views: resolve the live install mode from the real environment.
export function currentInstallMode(win: Window = window, nav: Navigator = navigator): InstallMode {
  return installMode({
    standalone: isStandalone(win, nav),
    hasPrompt: hasDeferredPrompt(),
    platform: detectPlatform({ ua: nav.userAgent, maxTouchPoints: nav.maxTouchPoints }),
  });
}
